import React from 'react';
import type { SyncSummaryCard } from '@/api';

interface SyncPreviewProps {
  articleTitle: string;
  sourceUrl: string;
  sourceName?: string;
  thumbnailUrl?: string | null;
  publishedAt?: string | null;
  summaryCards: SyncSummaryCard[];
  /** When true, shows card-by-card reader like Expo app */
  readerMode?: boolean;
}

function PreviewCard({ card }: { card: SyncSummaryCard }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2">
        <span className="text-xs font-bold uppercase tracking-wide text-indigo-600">{card.sub_title}</span>
        <h3 className="text-base font-bold text-slate-800 mt-0.5">{card.title}</h3>
      </div>
      <div className="space-y-3">
        {card.sections.map((s, i) =>
          s.type === 'text' ? (
            <p key={i} className="text-sm text-slate-600 leading-relaxed">{s.content}</p>
          ) : (
            <img
              key={i}
              src={s.content}
              alt=""
              className="max-w-full h-40 object-contain rounded-lg border border-slate-200"
            />
          ),
        )}
      </div>
      {card.key_takeaway && (
        <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
          <p className="text-xs font-bold uppercase text-indigo-600 mb-1">Key takeaway</p>
          <p className="text-sm font-medium text-slate-800">{card.key_takeaway}</p>
        </div>
      )}
    </div>
  );
}

export function SyncPreview({
  articleTitle,
  sourceUrl,
  sourceName,
  thumbnailUrl,
  publishedAt,
  summaryCards,
  readerMode = false,
}: SyncPreviewProps) {
  const [cardIndex, setCardIndex] = React.useState(0);
  const currentCard = summaryCards[cardIndex];

  return (
    <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500 uppercase">Preview (app-like)</span>
        {readerMode && summaryCards.length > 1 && (
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={() => setCardIndex((i) => Math.max(0, i - 1))}
              disabled={cardIndex === 0}
              className="rounded border border-slate-300 px-2 py-1 text-sm disabled:opacity-40 hover:bg-white"
            >
              ← Prev
            </button>
            <span className="text-sm text-slate-600">
              {cardIndex + 1} / {summaryCards.length}
            </span>
            <button
              type="button"
              onClick={() => setCardIndex((i) => Math.min(summaryCards.length - 1, i + 1))}
              disabled={cardIndex === summaryCards.length - 1}
              className="rounded border border-slate-300 px-2 py-1 text-sm disabled:opacity-40 hover:bg-white"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800">{articleTitle}</h2>
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
            {sourceName && <span>{sourceName}</span>}
            {publishedAt && <span>• {new Date(publishedAt).toLocaleDateString()}</span>}
          </div>
          <a
            href={sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-indigo-600 hover:underline mt-1 inline-block"
          >
            Visit original →
          </a>
        </div>
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt=""
            className="w-full h-40 object-cover"
          />
        )}
        <div className="p-4">
          {readerMode && currentCard ? (
            <PreviewCard card={currentCard} />
          ) : (
            <div className="space-y-4">
              {summaryCards.map((card, i) => (
                <PreviewCard key={card.id || i} card={card} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
